import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/shared/Header";
import VideoUpload from "@/components/shared/VideoUpload";
import LiveRecordingPanel from "@/components/shared/LiveRecordingPanel";
import VideoCard from "@/components/shared/VideoCard";
import { useLanguage } from "@/context/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import videoApi from "@/lib/api";
import { getApiErrorMessage } from "@/lib/api";
import { prefetchPricingSnapshot } from "@/lib/pricingPrefetch";
import { useAuth } from "@/context/AuthContext";
import { Loader2, RefreshCw, FileVideo, Clock, Crown } from "lucide-react";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [optimisticVideos, setOptimisticVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [usage, setUsage] = useState(null);
  const [pricingOpening, setPricingOpening] = useState(false);
  const { t, language } = useLanguage();

  const isTerminalVideoStatus = useCallback((status) => ["completed", "failed", "cancelled"].includes(status), []);

  const mergeProgressSafely = useCallback((previousVideo, incomingVideo) => {
    if (!previousVideo || !incomingVideo) return incomingVideo;
    if (isTerminalVideoStatus(previousVideo.status) || isTerminalVideoStatus(incomingVideo.status)) {
      return incomingVideo;
    }

    const previousProgress = Number(previousVideo.progress || 0);
    const incomingProgress = Number(incomingVideo.progress || 0);
    if (incomingProgress >= previousProgress) {
      return incomingVideo;
    }

    return {
      ...incomingVideo,
      progress: previousProgress,
      processing_message: previousVideo.processing_message || incomingVideo.processing_message,
    };
  }, [isTerminalVideoStatus]);

  const filterVisibleVideos = useCallback((items = []) => items, []);

  const createOptimisticVideo = useCallback((uploadResult, uploadedFile) => ({
    id: uploadResult.video_id,
    original_filename: uploadedFile?.name || (language === "es" ? "Archivo recién subido" : "Recently uploaded file"),
    content_kind: uploadResult.content_kind === "document" ? "document" : "media",
    source_format: uploadedFile?.name?.split('.')?.pop?.()?.toLowerCase?.() || null,
    created_at: new Date().toISOString(),
    status: uploadResult.status === "processing" ? "processing" : "pending",
    progress: uploadResult.status === "processing" ? 10 : 0,
    duration_seconds: null,
    processing_message: language === "es"
      ? "La subida terminó. Ahora estamos iniciando el procesamiento."
      : "Upload finished. We’re now starting processing.",
    key_moments: null,
  }), [language]);

  const fetchVideos = useCallback(async ({ showErrorToast = true } = {}) => {
    try {
      const data = await videoApi.list();
      const visibleVideos = filterVisibleVideos(data.videos || []);
      setOptimisticVideos((prev) => {
        const now = Date.now();
        const realIds = new Set(visibleVideos.map((video) => video.id));
        const remaining = prev.filter((video) => {
          const ageMs = now - new Date(video.created_at).getTime();
          return !realIds.has(video.id) && ageMs < 10 * 60 * 1000;
        });
        const currentById = new Map([
          ...videos.map((video) => [video.id, video]),
          ...remaining.map((video) => [video.id, video]),
        ]);
        setVideos([
          ...remaining,
          ...visibleVideos
            .filter((video) => !remaining.some((pending) => pending.id === video.id))
            .map((video) => mergeProgressSafely(currentById.get(video.id), video)),
        ]);
        return remaining;
      });
    } catch (error) {
      console.error("Failed to fetch videos:", error);
      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar tus videos" : "Failed to load videos", language));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterVisibleVideos, language, mergeProgressSafely, videos]);

  const fetchUsage = useCallback(async () => {
    try {
      const data = await videoApi.getUsage();
      setUsage(data);
    } catch (error) {
      console.error("Failed to fetch usage:", error);
    }
  }, []);

  useEffect(() => {
    fetchVideos({ showErrorToast: false });
    fetchUsage();
  }, [fetchVideos, fetchUsage]);

  useEffect(() => {
    prefetchPricingSnapshot({ includeSubscription: true, viewerKey: user?.id || "guest" }).catch((error) => {
      console.error("Failed to prefetch pricing snapshot:", error);
    });
  }, [user?.id]);

  // Auto-refresh for processing videos
  useEffect(() => {
    const processingVideos = videos.filter(
      (v) =>
        v.status === "pending" ||
        v.status === "processing" ||
        v.status === "extracting_audio" ||
        v.status === "transcribing" ||
        v.status === "analyzing"
    );

    if (processingVideos.length > 0) {
      const interval = setInterval(() => {
        if (document.visibilityState !== "visible") {
          return;
        }
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          return;
        }
        fetchVideos({ showErrorToast: false });
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [videos, fetchVideos]);

  const handleUploadComplete = async (uploadResult, uploadedFile) => {
    const optimisticVideo = createOptimisticVideo(uploadResult, uploadedFile);
    setOptimisticVideos((prev) => [optimisticVideo, ...prev.filter((video) => video.id !== optimisticVideo.id)]);
    setVideos((prev) => [optimisticVideo, ...prev.filter((video) => video.id !== optimisticVideo.id)]);

    await fetchVideos({ showErrorToast: false });
    await fetchUsage();
  };

  const handleVideoDelete = (videoId) => {
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVideos({ showErrorToast: true });
    await fetchUsage();
  };

  const openPricingPage = async () => {
    setPricingOpening(true);
    try {
      await prefetchPricingSnapshot({ includeSubscription: true, force: true, viewerKey: user?.id || "guest" });
      navigate("/pricing");
    } finally {
      setPricingOpening(false);
    }
  };

  // Format minutes to hours and minutes
  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hrs === 0) return `${mins}m`;
    return `${hrs}h ${mins}m`;
  };

  const shouldHighlightUpgrade = Boolean(usage && !usage.is_admin && ((usage.membership || "free") === "free" || usage.lifetime_limit));
  const showTestTranscriptionEngineSelector = Boolean(
    usage?.is_admin && typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com")
  );

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />

      <main className="container mx-auto max-w-6xl px-4 pt-24 pb-12 sm:px-6">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("dashboardTitle")}</h1>
            <p className="text-muted-foreground mt-1">
              {t("dashboardSubtitle")}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="w-full sm:w-auto"
            data-testid="refresh-btn"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            {t("refresh")}
          </Button>
        </div>

        {/* Daily Usage Card */}
        {usage && !usage.is_admin && (
          <Card className="bg-card border-border p-4 mb-6">
            <div className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">
                  {usage.lifetime_limit
                    ? (language === "es" ? "Free Tier de por vida" : "Lifetime Free Tier")
                    : (language === "es" ? "Uso Diario" : "Daily Usage")}
                </span>
                {usage.membership && usage.membership !== "free" && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-400 capitalize">
                    {usage.membership}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <span className="text-sm text-muted-foreground">
                  {formatTime(usage.minutes_used)} / {formatTime(usage.daily_limit)}
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={openPricingPage}
                  disabled={pricingOpening}
                  className={shouldHighlightUpgrade
                    ? "speaker-edit-cta dashboard-upgrade-cta w-full sm:w-auto"
                    : "h-8 w-full text-xs border-violet-500/50 text-violet-400 hover:bg-violet-500/10 sm:h-7 sm:w-auto"
                  }
                  data-testid="upgrade-btn"
                >
                  {pricingOpening ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown className={shouldHighlightUpgrade ? "mr-2 h-4 w-4" : "mr-1 h-3 w-3"} />}
                  {pricingOpening
                    ? (language === "es" ? "Abriendo planes..." : "Opening pricing...")
                    : shouldHighlightUpgrade
                    ? (language === "es" ? "Suscribirse" : "Subscribe")
                    : (language === "es" ? "Mejorar" : "Upgrade")}
                </Button>
              </div>
            </div>
            <Progress 
              value={(usage.minutes_used / usage.daily_limit) * 100} 
              className="h-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {usage.lifetime_limit
                ? (usage.free_trial_used
                    ? (language === "es" ? "Ya usaste tu único procesamiento gratis de 30 minutos. Haz upgrade para seguir." : "You already used your one free 30-minute processing. Upgrade to continue.")
                    : (language === "es" ? "Tienes 1 único video/audio gratis de hasta 30 minutos." : "You have 1 lifetime free video/audio up to 30 minutes."))
                : (language === "es" 
                    ? `Te quedan ${formatTime(usage.remaining_minutes)} hoy`
                    : `${formatTime(usage.remaining_minutes)} remaining today`)
              }
            </p>
          </Card>
        )}

        {/* Admin Badge */}
        {usage && usage.is_admin && (
          <Card className="bg-primary/10 border-primary/30 p-3 mb-6">
            <p className="text-sm text-primary font-medium text-center">
              {language === "es" ? "👑 Admin - Uso Ilimitado" : "👑 Admin - Unlimited Usage"}
            </p>
          </Card>
        )}

        {/* Upload Section */}
        <div className="mb-12">
          {usage?.free_upload_blocked ? (
            <Card className="bg-card border-amber-500/30 p-6" data-testid="free-tier-upload-blocked-card">
              <div className="flex items-start justify-between gap-4 flex-col md:flex-row md:items-center">
                <div>
                  <p className="text-lg font-semibold text-foreground">
                    {language === "es" ? "Tu prueba gratis ya fue utilizada" : "Your free trial has already been used"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {language === "es"
                      ? "El plan free solo permite 1 video o audio de hasta 30 minutos en toda la vida de la cuenta. Haz upgrade para seguir procesando archivos."
                      : "The free tier only allows 1 lifetime video or audio up to 30 minutes. Upgrade to keep processing files."}
                  </p>
                </div>
                <Button onClick={openPricingPage} disabled={pricingOpening} data-testid="free-tier-upgrade-button">
                  {pricingOpening ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
                  {pricingOpening ? (language === "es" ? "Abriendo planes..." : "Opening pricing...") : (language === "es" ? "Hacer upgrade" : "Upgrade now")}
                </Button>
              </div>
            </Card>
          ) : (
            <VideoUpload
              onUploadComplete={handleUploadComplete}
              showTestTranscriptionEngineSelector={showTestTranscriptionEngineSelector}
            />
          )}

          <div className="mt-6" data-testid="dashboard-live-recording">
            <LiveRecordingPanel onRecordingUploaded={handleUploadComplete} />
          </div>
        </div>

        {/* Videos List */}
        <div>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-semibold">{t("yourVideos")}</h2>
            <span className="text-sm text-muted-foreground">
              {videos.length} {videos.length !== 1 ? t("videos") : t("video")}
            </span>
          </div>

          {loading ? (
            <Card className="bg-card border-border p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </Card>
          ) : videos.length === 0 ? (
            <Card className="bg-card border-border p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <FileVideo className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">{t("noVideosYet")}</h3>
              <p className="text-muted-foreground text-sm">
                {t("noVideosDesc")}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} onDelete={handleVideoDelete} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
